import {FC, ReactNode} from 'react'

import {HEADER_HEIGHT_DESKTOP, PRICING_SIDEBAR_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {useCountryCardsList} from './hooks/useCountryCardsList'
import {PricingCalculatorCtaButton} from './PricingCalculatorCtaButton'
import {JurisdictionPricingInfo, PricingCountryCheckboxList} from './PricingCountryCheckboxList'

export type PricingCountryCalculatorProps = {
  selectedJurisdictions: Jurisdiction[]
  setSelectedJurisdictions: (jurisdictions: Jurisdiction[]) => void
  linkHref: string
  trackClickEvent: () => void
  startButtonText: string
  continueButtonText: string
  estimationCard: ReactNode
  discountBanner?: ReactNode
  getJurisdictionPricing: (
    jurisdiction: Jurisdiction,
    currency: Currency,
  ) => JurisdictionPricingInfo
  allowedJurisdictions?: Jurisdiction[]
  allowEuMemberSelection: boolean
}

export const PricingCountryCalculator: FC<PricingCountryCalculatorProps> = ({
  selectedJurisdictions,
  setSelectedJurisdictions,
  linkHref,
  trackClickEvent,
  startButtonText,
  continueButtonText,
  estimationCard,
  discountBanner,
  getJurisdictionPricing,
  allowedJurisdictions,
  allowEuMemberSelection,
}) => {
  const className = useUniqueClassName('pricing-sidebar')
  const countryCardsList = useCountryCardsList(8, allowedJurisdictions)

  return (
    <>
      <div className="wrapper">
        <div className="list">
          <PricingCountryCheckboxList
            selectedJurisdictions={selectedJurisdictions}
            setSelectedJurisdictions={setSelectedJurisdictions}
            countryCardsList={countryCardsList}
            getJurisdictionPricing={getJurisdictionPricing}
            allowEuMemberSelection={allowEuMemberSelection}
          />
        </div>
        <div className={className}>
          {estimationCard}
          <div className="button-wrapper">
            <PricingCalculatorCtaButton
              linkHref={linkHref}
              trackClickEvent={trackClickEvent}
              startButtonText={startButtonText}
              continueButtonText={continueButtonText}
              hasSelectedJurisdictions={selectedJurisdictions.length > 0}
            />
          </div>
          {discountBanner}
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
        }

        .list {
          flex: 1;
        }

        .${className} {
          position: sticky;
          top: calc(${HEADER_HEIGHT_DESKTOP} + ${theme.spacing[4]});
          right: 0;
          flex: 0 0 ${PRICING_SIDEBAR_WIDTH};
          height: 100%;
          margin-left: ${theme.spacing[7]};
        }

        @media ${TABLET_SCREENSIZE} {
          .${className} {
            top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[6]};
          }
        }
      `}</style>
      <style jsx global>{`
        .${className}:has(.modal-stable) {
          z-index: 3;
        }
      `}</style>
    </>
  )
}
